import React, { FC, HTMLProps, memo, useEffect, useMemo, useRef, useState } from 'react';
import { isBefore } from 'date-fns';

import DefectRow, { DefectPositie } from '@/components/defectRow/DefectRow';
import { searchCriteriaForEenheden } from '@/components/search/v1/utils/helpers';
import { parseValidEnumeration, parseValidRange } from '@/components/search/v1/utils/searchValidations';
import type { DefectenFilter } from '@/components/sidebar/defecten/defectenFilter/defectenFilterModel';
import useSidebarStore from '@/components/sidebar/hooks/useSidebarStore';
import {
  nummerComparator,
  SorteringRichting,
  SorteringType,
  tijdComparator,
} from '@/components/sidebar/sortField/useSorting';
import { BronType, Defect, DefectSoort, DefectStatus, getDefectBron, getDefectSoort } from '@/types/Defecten';
import { endOfTomorrow2AM } from '@/utils/dateUtils';
import { combineFilters } from '@/utils/functions';
import { createContextMenuData } from '@/utils/helpers';
import { useVirtualizer } from '@tanstack/react-virtual';

import styles from './Defecten.module.css';

const eenheidRangeFilter = (fromEenheidValue: number, toEenheidValue: number, defect: Defect) => {
  const eenheidNummer = Number(defect.eenheidNummer);
  return eenheidNummer >= fromEenheidValue && eenheidNummer <= toEenheidValue;
};

const filterDefectOpEenheidNummer = (eenheidnummer?: string) => (defect: Defect) => {
  if (!eenheidnummer) {
    return true;
  }
  const validEnumeration = parseValidEnumeration(eenheidnummer);
  const validEenheidRange = parseValidRange(eenheidnummer);
  if (validEnumeration) {
    return validEnumeration.includes(Number(defect.eenheidNummer));
  }
  if (validEenheidRange) {
    const [fromEenheidValue, toEenheidValue] = validEenheidRange;
    return eenheidRangeFilter(fromEenheidValue, toEenheidValue, defect);
  }
  return defect.eenheidNummer === eenheidnummer;
};

const filterDefectOpMaterieelType = (materieelTypen?: string[]) => (defect: Defect) => {
  if (!materieelTypen || materieelTypen.length === 0) {
    return true;
  }
  const materieelTypeDefect = defect.materieelSoort ? defect.materieelSoort : '';
  return materieelTypen.some((mt) => mt.toUpperCase() === materieelTypeDefect.toUpperCase());
};

const filterDefectWordtNietInzetbaar = (defectSoorten: DefectSoort[], defect: Defect) => {
  if (defectSoorten.includes('DEFECT_NIET_INZETBAAR')) {
    const lastDayOfViewAt2AM = endOfTomorrow2AM(new Date());
    const nietInzetbaarVanafTijd = new Date(defect.nietMeerInzetbaarVanaf as string).getTime();
    return !!defect.nietMeerInzetbaarVanaf && isBefore(nietInzetbaarVanafTijd, lastDayOfViewAt2AM.getTime());
  }
  return false;
};

const filterDefectOpDefectSoort = (defectSoorten?: DefectSoort[]) => (defect: Defect) => {
  if (!defectSoorten || defectSoorten.length === 0) {
    return true;
  }

  if (defectSoorten.some((ds) => ds === defect.soort)) {
    return true;
  }

  // In geval van filteren op defect-niet-inzetbaar, dan naast gelijke defectsoort ook andere defecten
  // teruggeven die binnen de bijsturingsperiode niet inzetbaar gaan worden
  return filterDefectWordtNietInzetbaar(defectSoorten, defect);
};

const filterDefectOpDefectStatus = (defectStatussen?: DefectStatus[]) => (defect: Defect) => {
  if (!defectStatussen || defectStatussen.length === 0) {
    return true;
  }

  return defectStatussen.some((ds) => ds === getDefectSoort(defect));
};

const filterDefectOpBronType = (defectBronnen?: BronType[]) => (defect: Defect) => {
  if (!defectBronnen || defectBronnen.length === 0) {
    return true;
  }

  return defectBronnen.some((ds) => ds === getDefectBron(defect));
};

const maakBijbehorendeNietInzetbaarDefecten = (defecten: Defect[]): Defect[] => {
  const alleDefecten: Defect[] = [];
  defecten.forEach((defect) => {
    alleDefecten.push(defect);
    if (defect.nietMeerInzetbaarVanaf && defect.soort != 'DEFECT_NIET_INZETBAAR') {
      const bijbehorendNietInzetbaarDefect: Defect = {
        ...defect,
        soort: 'DEFECT_NIET_INZETBAAR',
      };
      alleDefecten.push(bijbehorendNietInzetbaarDefect);
    }
  });
  return alleDefecten;
};

const defectSoortComparator = (
  defectSoortA: DefectSoort,
  defectSoortB: DefectSoort,
  sortDirection: SorteringRichting | undefined,
  fallbackComparator?: () => number,
) => {
  if (defectSoortA === defectSoortB) {
    return fallbackComparator ? fallbackComparator() : 0;
  }

  const defectOrder = {
    DEFECT_NIET_INZETBAAR: 1,
    BEPERKING_IN_TREINDIENST: 2,
    GEEN_BEPERKINGEN: 3,
  };

  const directionMultiplier = sortDirection === SorteringRichting.UP ? -1 : 1;
  return defectOrder[defectSoortA] > defectOrder[defectSoortB] ? directionMultiplier : -directionMultiplier;
};

const defectComparator = (
  defectA: Defect,
  defectB: Defect,
  sortType: SorteringType | undefined,
  sortDirection: SorteringRichting | undefined,
) => {
  if (defectA.gereedgemeld && !defectB.gereedgemeld) {
    return 1;
  }
  if (!defectA.gereedgemeld && defectB.gereedgemeld) {
    return -1;
  }

  const fallbackComparator = tijdComparator(defectA.beginTijd, defectB.beginTijd, SorteringRichting.UP);

  if (sortType === SorteringType.BEGIN_TIJD) {
    return tijdComparator(defectA.beginTijd, defectB.beginTijd, sortDirection);
  }
  if (sortType === SorteringType.EENHEID_NUMMER) {
    return nummerComparator(
      Number(defectA.eenheidNummer),
      Number(defectB.eenheidNummer),
      sortDirection,
      () => fallbackComparator,
    );
  }
  if (sortType === SorteringType.DEFECT_SOORT) {
    return defectSoortComparator(defectA.soort, defectB.soort, sortDirection, () => fallbackComparator);
  }
  if (sortType === SorteringType.NIET_MEER_INZETBAAR_TIJD) {
    return tijdComparator(
      defectA.nietMeerInzetbaarVanaf,
      defectB.nietMeerInzetbaarVanaf,
      sortDirection,
      () => fallbackComparator,
    );
  }

  // Default sorteer op begintijd up
  return fallbackComparator;
};

const filterEnSorteerDefecten = (defecten: Defect[] | undefined, currentFilter: DefectenFilter) => {
  if (!defecten) {
    return [];
  }

  const eenheidFilter = filterDefectOpEenheidNummer(currentFilter.eenheidnummer);
  const materieelTypenFilter = filterDefectOpMaterieelType(currentFilter.materieelTypen);
  const defectSoortenFilter = filterDefectOpDefectSoort(currentFilter.defectSoorten);
  const defectStatussenFilter = filterDefectOpDefectStatus(currentFilter.defectStatussen);
  const defectBronFilter = filterDefectOpBronType(currentFilter.defectBronnen);

  const gefilterdeEnGesorteerdeDefecten = defecten
    .filter(
      combineFilters(eenheidFilter, materieelTypenFilter, defectSoortenFilter, defectStatussenFilter, defectBronFilter),
    )
    .sort((defectA, defectB) =>
      defectComparator(defectA, defectB, currentFilter.sorteringType, currentFilter.sorteringRichting),
    );
  return maakBijbehorendeNietInzetbaarDefecten(gefilterdeEnGesorteerdeDefecten);
};

const DefectenTable: FC<
  {
    defecten: Defect[];
    panelMenuInformation;
  } & HTMLProps<HTMLDivElement>
> = ({ defecten, panelMenuInformation, ...rest }) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [selectedDefecten, setSelectedDefecten] = useState<Defect[]>([]);
  const activePanel = useSidebarStore((state) => state.activePanel);
  const setActivePanel = useSidebarStore((state) => state.setActivePanel);
  const setData = useSidebarStore((state) => state.setData);

  const rowVirtualizer = useVirtualizer({
    count: defecten.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 50,
  });

  const defectPositie: (index: number) => DefectPositie = (index) => {
    const huidigDefectId = defecten[index].id;

    if (defecten[index - 1]?.id !== huidigDefectId) {
      return defecten[index + 1]?.id !== huidigDefectId ? 'enkelvoudig' : 'eerste';
    }
    return 'laatste';
  };

  function handleOnDefectClick(event: React.MouseEvent<HTMLDivElement>, defect: Defect) {
    panelMenuInformation.setActivePanel('Defecten');
    setActivePanel('Defecten');
    event.preventDefault();
    if (event.button === 2) {
      let defectenVoorMwd: Defect[];
      if (!selectedDefecten.includes(defect)) {
        setSelectedDefecten([defect]);
        defectenVoorMwd = [defect];
      } else {
        defectenVoorMwd = selectedDefecten;
      }
      const contextMenuData = createContextMenuData(event, [
        searchCriteriaForEenheden([...new Set(defectenVoorMwd.map((value) => value.eenheidNummer))]),
      ]);
      panelMenuInformation.contextMenuInformationCallback(contextMenuData);
    } else if (event.ctrlKey) {
      setSelectedDefecten((prev) => {
        if (prev.includes(defect)) {
          return prev.filter((id) => id !== defect);
        }
        return [...prev, defect];
      });
    } else if (event.shiftKey) {
      const selectedDefectIndex = defecten.findIndex((d) => d.id === selectedDefecten[0].id);
      const clickedDefectIndex = defecten.findIndex((d) => d.id === defect.id);
      const start = Math.min(selectedDefectIndex, clickedDefectIndex);
      const end = Math.max(selectedDefectIndex, clickedDefectIndex);
      setSelectedDefecten(defecten.slice(start, end + 1));
    } else if (selectedDefecten.includes(defect)) {
      setSelectedDefecten([]);
    } else {
      setSelectedDefecten([defect]);
    }
  }

  function handleOnDefectKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    panelMenuInformation.setActivePanel('Defecten');
    setActivePanel('Defecten');
    event.preventDefault();
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      setSelectedDefecten(defecten);
    }
    // Legacy code die niet meer in V2 worden getrigger
    if (event.altKey && event.code === 'KeyT') {
      panelMenuInformation.handleSelectieUitPanel([
        searchCriteriaForEenheden([...new Set(selectedDefecten.map((value) => value.eenheidNummer))]),
      ]);
      panelMenuInformation.setActivePanel(undefined);
    }
  }

  useEffect(() => {
    setData('defecten', selectedDefecten);
  }, [selectedDefecten]);

  useEffect(() => {
    if (panelMenuInformation.activePanel !== 'Defecten' || activePanel !== 'Defecten') {
      setSelectedDefecten([]);
    }
  }, [panelMenuInformation.activePanel, activePanel]);

  return (
    defecten && (
      <div ref={parentRef} className={styles.defectenScrollableStyle} {...rest}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => (
            <DefectRow
              key={virtualItem.key}
              tabIndex={0}
              defect={defecten[virtualItem.index]}
              positie={defectPositie(virtualItem.index)}
              onClick={(event) => handleOnDefectClick(event, defecten[virtualItem.index])}
              onContextMenu={(event) => handleOnDefectClick(event, defecten[virtualItem.index])}
              onKeyDown={(event) => handleOnDefectKeyDown(event)}
              data-selected={selectedDefecten.some((value) => defecten[virtualItem.index].id === value.id)}
              data-dtname='defectenlijst-element'
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
                background:
                  ((panelMenuInformation && panelMenuInformation.activePanel === 'Defecten') ||
                    activePanel === 'Defecten') &&
                  selectedDefecten.some((value) => defecten[virtualItem.index].id === value.id)
                    ? 'var(--tractie-color-background-selected-default)'
                    : '',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      </div>
    )
  );
};

const MemoizedDefectenTable = memo(DefectenTable);

const DefectenLijst: FC<
  {
    defecten: Defect[];
    filter: DefectenFilter;
    panelMenuInformation;
  } & HTMLProps<HTMLDivElement>
> = ({ defecten, filter, panelMenuInformation, ...rest }) => {
  const gefilterdeEnGesorteerdeData = useMemo(() => filterEnSorteerDefecten(defecten, filter), [defecten, filter]);

  return (
    <MemoizedDefectenTable
      defecten={gefilterdeEnGesorteerdeData}
      {...rest}
      panelMenuInformation={panelMenuInformation}
    />
  );
};

export default DefectenLijst;
